/**
 * CSV Data Loader Service
 * Loads and parses USD.P token transfer data from sampledata.csv
 */

import { FormattedTransfer } from './bscScanService';

export interface CSVTransferRow {
  transactionHash: string;
  blockNumber: string;
  unixTimestamp: string;
  dateTime: string;
  from: string;
  to: string;
  quantity: string;
  method: string;
}

class CSVDataLoader {
  private static instance: CSVDataLoader;
  private cachedData: FormattedTransfer[] | null = null;
  private isLoading = false;

  static getInstance(): CSVDataLoader {
    if (!this.instance) {
      this.instance = new CSVDataLoader();
    }
    return this.instance;
  }

  private constructor() {
    console.log('🔧 CSVDataLoader initialized');
  }

  /**
   * Parse CSV content into structured data
   */
  private parseCSV(csvContent: string): CSVTransferRow[] {
    const lines = csvContent.split('\n').filter(line => line.trim());
    const transfers: CSVTransferRow[] = [];

    // Skip header row (index 0)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      
      // Parse CSV with quotes
      const regex = /"([^"]*)"|([^,]+)/g;
      const values: string[] = [];
      let match;
      
      while ((match = regex.exec(line)) !== null) {
        values.push(match[1] || match[2] || '');
      }

      if (values.length >= 8) {
        transfers.push({
          transactionHash: values[0],
          blockNumber: values[1],
          unixTimestamp: values[2],
          dateTime: values[3],
          from: values[4],
          to: values[5],
          quantity: values[6].replace(/,/g, ''), // Remove commas from numbers
          method: values[7]
        });
      }
    }

    return transfers;
  }

  /**
   * Convert CSV row to FormattedTransfer
   */
  private convertToFormattedTransfer(row: CSVTransferRow): FormattedTransfer {
    return {
      id: `csv_${row.transactionHash}`,
      txHash: row.transactionHash,
      from: row.from,
      to: row.to,
      amount: parseFloat(row.quantity).toFixed(6),
      tokenSymbol: 'USDP',
      tokenName: 'USD.P E-Pay Dollar',
      tokenLogo: '/USDPlogo.png',
      blockNumber: parseInt(row.blockNumber),
      timestamp: parseInt(row.unixTimestamp) * 1000, // Convert to milliseconds
      gasUsed: '21000', // Default gas used
      gasPrice: '5.0000', // Default gas price in gwei
      status: 'confirmed' as const,
      chainId: 56, // BSC Mainnet
      networkName: 'BNB Smart Chain',
      type: 'transfer' as const
    };
  }

  /**
   * Load transfers from CSV file
   */
  async loadTransfers(): Promise<FormattedTransfer[]> {
    // Return cached data if available
    if (this.cachedData) {
      console.log(`📦 Returning ${this.cachedData.length} cached CSV transfers`);
      return this.cachedData;
    }

    // Prevent multiple simultaneous loads
    if (this.isLoading) {
      console.log('⏳ CSV data is already being loaded...');
      // Wait for the current load to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      return this.cachedData || [];
    }

    this.isLoading = true;

    try {
      console.log('📂 Loading USD.P transfers from CSV file...');
      
      // Fetch the CSV file from the public directory
      const response = await fetch('/sampledata.csv');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch CSV file: ${response.status}`);
      }

      const csvContent = await response.text();
      console.log('📄 CSV file loaded successfully');

      // Parse CSV
      const csvRows = this.parseCSV(csvContent);
      console.log(`📊 Parsed ${csvRows.length} transfers from CSV`);

      // Convert to FormattedTransfer objects
      const formattedTransfers = csvRows.map(row => this.convertToFormattedTransfer(row));
      
      // Cache the data
      this.cachedData = formattedTransfers;
      console.log(`✅ Loaded ${formattedTransfers.length} USD.P transfers from CSV`);

      return formattedTransfers;
    } catch (error) {
      console.error('❌ Failed to load CSV data:', error);
      return [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get paginated transfers
   */
  async getTransfers(page: number = 1, limit: number = 100): Promise<FormattedTransfer[]> {
    const allTransfers = await this.loadTransfers();
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    
    return allTransfers.slice(startIndex, endIndex);
  }

  /**
   * Get transfers for a specific address
   */
  async getTransfersByAddress(address: string, limit: number = 100): Promise<FormattedTransfer[]> {
    const allTransfers = await this.loadTransfers();
    const addressLower = address.toLowerCase();
    
    return allTransfers
      .filter(transfer =>
        transfer.from.toLowerCase() === addressLower ||
        transfer.to.toLowerCase() === addressLower
      )
      .slice(0, limit);
  }

  /**
   * Clear cache to force reload
   */
  clearCache(): void {
    this.cachedData = null;
    console.log('🗑️ CSV cache cleared');
  }

  /**
   * Get statistics
   */
  async getStats(): Promise<{
    totalTransfers: number;
    totalVolume: number;
    uniqueAddresses: number;
  }> {
    const transfers = await this.loadTransfers();
    const addresses = new Set<string>();
    let totalVolume = 0;

    transfers.forEach(transfer => {
      addresses.add(transfer.from.toLowerCase());
      addresses.add(transfer.to.toLowerCase());
      totalVolume += parseFloat(transfer.amount);
    });

    return {
      totalTransfers: transfers.length,
      totalVolume,
      uniqueAddresses: addresses.size
    };
  }
}

// Export singleton instance
export const csvDataLoader = CSVDataLoader.getInstance();